import { Button, Divider, Form, Justify, Label, Row, TextFormField, type IFormRender } from '@busybusy/webapp-react-ui';
import { EmptyState, Well } from 'components';
import EquipmentInspectionChecklistPickerFormField from 'components/domain/equipment/EquipmentInspectionChecklistPickerFormField/EquipmentInspectionChecklistPickerFormField';
import useEquipmentChecklistRoutines from 'containers/equipment-inspections/hooks/useEquipmentChecklistRoutines';
import type { IManageEquipmentRowInfo } from 'containers/manage-euipment/ManageEquipment';
import { useLoaderToastPromise } from 'hooks/promise';
import { t } from 'utils/localize';
import { v_require } from 'utils/validations';
import v_integers_only from 'utils/validations/v_number_integers_only';

const MAX_ROUTINES = 3;

interface ManageEquipmentBulkAddRoutineInspectionsForm {
  checklistId: string | undefined;
  intervalDays: string | undefined;
}

interface IManageEquipmentBulkAddRoutineInspectionsProps {
  checkedRows: IManageEquipmentRowInfo[];
  onClose: () => void;
  onSubmit: () => void;
}

export function ManageEquipmentBulkAddRoutineInspections(props: IManageEquipmentBulkAddRoutineInspectionsProps) {
  const { checkedRows, onClose, onSubmit } = props;
  const { createEquipmentChecklistRoutine } = useEquipmentChecklistRoutines();

  const atLimit = checkedRows.some((row) => row.equipment.routines.length >= MAX_ROUTINES);

  const submitBulkAdd = useLoaderToastPromise((proposedFormData: ManageEquipmentBulkAddRoutineInspectionsForm) => {
    const { checklistId } = proposedFormData;
    if (!checklistId) return Promise.resolve(); // should not happen due to form validation, but guard just in case

    return Promise.all(
      checkedRows.map(({ equipment }) =>
        createEquipmentChecklistRoutine({
          equipmentId: equipment.id,
          equipmentInspectionChecklistId: checklistId,
          recurringDays: parseInt(proposedFormData.intervalDays ?? '0', 10),
          createdOn: new Date().toISOString(),
        })
      )
    ).then(() => {
      onSubmit();
    });
  });

  if (!checkedRows.length) {
    return <EmptyState title={t('No Equipment Selected')} />;
  }

  if (atLimit) {
    return (
      <div>
        <LimitReachedNotification />
        <Button className="pl-5 pr-5 mt-4" type="secondary" onClick={onClose}>
          {t('Close')}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <AddingRoutineInspectionsNotification />
      <Form
        data={{ checklistId: undefined, intervalDays: undefined } as ManageEquipmentBulkAddRoutineInspectionsForm}
        onSubmit={submitBulkAdd}
        allowMultipleSubmissions
        className="mt-8 manage-equipment-bulk-add-routine-inspections-form"
        render={(form: IFormRender<ManageEquipmentBulkAddRoutineInspectionsForm>) => (
          <>
            <Row justify={Justify.SPACE_BETWEEN}>
              <Label className="routine-checklist-picker">{t('Inspection')}</Label>
              <Label className="routine-interval-field">{t('Interval (Days)')}</Label>
            </Row>
            <Divider className={'mb-4'} />
            <Row justify={Justify.SPACE_BETWEEN}>
              <EquipmentInspectionChecklistPickerFormField
                name="checklistId"
                form={form}
                className="routine-checklist-picker"
                validations={[{ validation: v_require }]}
              />
              <TextFormField
                name="intervalDays"
                form={form}
                className="routine-interval-field"
                validations={[{ validation: v_require }, { validation: v_integers_only }]}
              />
            </Row>
            <Button className="pl-5 pr-5 mt-4" type="primary" onClick={form.handleSubmit}>
              {t('Save')}
            </Button>
          </>
        )}
      />
    </div>
  );
}

function LimitReachedNotification() {
  return (
    <Well theme="danger" className="mb-4">
      <h4 className="fw-bold">{t('Limit Reached')}</h4>
      <h4>
        {t(
          'One or more of the selected equipment already has the maximum limit of three routine inspections assigned to it. You will need to remove at least one routine inspection in order to add a new one.'
        )}
      </h4>
    </Well>
  );
}

function AddingRoutineInspectionsNotification() {
  return (
    <Well theme="primary" className="mb-4">
      <h4 className="fw-bold">{t('Adding Routine Inspections')}</h4>
      <h4>
        {t(
          'Adding a new routine inspection will not remove or edit any existing routine inspections already attached to the selected equipment.'
        )}
      </h4>
    </Well>
  );
}
